import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, TemplateRef, ViewEncapsulation, forwardRef, Optional, SkipSelf, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { ChevronDown, Clock } from "../icon/icon-map";
import { PhxTimePickerFormat, PhxTimePickerSize } from "./phx-time-picker.type";
import mixinDisabled from "../../mixins/disabled";
import { PHOENIX_CONTEXTS, PhxContextService } from '../../services/phx-context-service';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../locale/phx-locale.token';

const baseTimePickerMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-time-picker',
  templateUrl: './phx-time-picker.component.html',
  styleUrls: ['./phx-time-picker.component.scss'],
  host: {
    class: 'phx-time-picker',
    '[class.phx-filter]': 'isInFilterRow',
    '[class.phx-time-picker-error]': 'shouldShowError',
    '[attr.phx-non-default-selected]': '!isDefaultSelection',
  },
  inputs: ['phxDisabled'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxTimePickerComponent),
      multi: true
    },
  ]
})
export class PhxTimePickerComponent extends baseTimePickerMixin implements ControlValueAccessor {
  @Input() public phxTimePickerFormat: PhxTimePickerFormat = 'default';
  @Input() public phxIsRangeEnabled: boolean = false;
  @Input() public phxValue: Date | [Date, Date];
  @Input() public phxShowInput: boolean = false;
  @Input() public phxSize: PhxTimePickerSize = 'medium';
  @Input() public phxPlaceholder: string;
  // TODO: need to tackle this in this ticket FP-2740
  @Input() public phxDisableBefore?: Date;
  @Input() public phxDisableAfter?: Date;
  @Input() public phxInputSuffix: TemplateRef<any>;
  @Input() public phxHasError: boolean = false;
  @Input() public phxDisabledHours: number[] = [];
  @Input() public phxDisabledMinutes: number[] = [];
  @Input() public phxDefaultSelection: Date | [Date, Date] | null = null;

  public clockIcon: string = Clock;
  public chevronDown: string = ChevronDown;
  public isDropdownOpen: boolean = false;
  public readonly phxStyleType: PHOENIX_CONTEXTS;

  private onChange: (value: Date | [Date, Date]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() @SkipSelf() contextService: PhxContextService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  public get isInFilterRow(): boolean {
    return this.phxStyleType === PHOENIX_CONTEXTS.FILTER;
  }

  public get isDefaultSelection(): boolean {
    if (!this.phxDefaultSelection && this.phxValue == null) {
      return true;
    }
    if (this.phxIsRangeEnabled && Array.isArray(this.phxValue) && Array.isArray(this.phxDefaultSelection)) {
      return this.isSameTime(this.phxValue[0], this.phxDefaultSelection[0]) &&
             this.isSameTime(this.phxValue[1], this.phxDefaultSelection[1]);
    }
    if (this.phxValue && !Array.isArray(this.phxValue) && !Array.isArray(this.phxDefaultSelection)) {
      return this.isSameTime(this.phxValue, this.phxDefaultSelection);
    }
    return false;
  }

  public get shouldShowError(): boolean {
    return this.phxHasError;
  }

  /**
   * Use 24 Hour clock if either:
   * - The locale uses a 24-hour clock (highest priority), or
   * - `phxTimePickerFormat` is set to '24H'.
   */
  public get effectiveTimePickerFormat(): PhxTimePickerFormat {
    if (this.phxLocale?.uses24HourClock?.()) {
      return '24H';
    }
    if (this.phxTimePickerFormat === '24H') {
      return '24H';
    }
    return 'default';
  }

  public writeValue(value: Date | [Date, Date]): void {
    if (value === null || value === undefined) {
      this.phxValue = null;
    } else {
      this.phxValue = value;
    }
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: (value: Date | [Date, Date]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
  }

  public onVisibleChange(value: boolean): void {
    this.isDropdownOpen = value;
    if(!value) {
      this.onTouched();
    }
  }

  public valueChange(value: Date): void {
    if (this.phxIsRangeEnabled) {
      if (!Array.isArray(this.phxValue)) {
        this.phxValue = [value, value];
      } else {
        this.phxValue[0] = value;
      }
      this.onChange(this.phxValue);
    } else {
      this.phxValue = value;
      this.onChange(new Date(value));
    }
  }

  public onStartTimeChange(value: Date) {
    if (this.phxIsRangeEnabled) {
      if (Array.isArray(this.phxValue)) {
        this.phxValue[0] = value;
      } else {
        this.phxValue = [value, this.phxValue as Date];
      }
      this.onChange([...this.phxValue]);
      return;
    } else {
      this.phxValue = value;
    }
    this.onChange(this.phxValue);
  }

  public onEndTimeChange(value: Date): void {
    if (Array.isArray(this.phxValue)) {
      this.phxValue[1] = value;
    } else {
      this.phxValue = [this.phxValue as Date, value];
    }
    this.onChange([...this.phxValue]);
  }

  public formatDateToTimeString(date: Date | [Date, Date]): string {
    if (!date || Array.isArray(date)) {
      return null;
    }
    date = new Date(date as Date);

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;

    if (this.effectiveTimePickerFormat === '24H') {
      const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
      return `${formattedHours}:${formattedMinutes}`;
    } else {
      const period = hours >= 12 ? 'PM' : 'AM';
      const adjustedHours = hours % 12 || 12;
      const formattedHours = adjustedHours < 10 ? `0${adjustedHours}` : `${adjustedHours}`;
      return `${formattedHours}:${formattedMinutes} ${period}`;
    }
  }

  public onClear(): void {
    this.phxValue = null;
    this.onChange(null);
  }

  public onReset(): void {
    if (this.phxDefaultSelection) {
      this.phxValue = Array.isArray(this.phxDefaultSelection)
        ? [...this.phxDefaultSelection]
        : this.phxDefaultSelection;
      this.onChange(this.phxValue);
    } else {
      this.onClear();
    }
    this.cdr.detectChanges();
  }

  public onBlur(): void {
    if(!this.isDropdownOpen) {
      this.onTouched();
    }
  }

  private isSameTime(a: Date, b: Date): boolean {
    if (!a || !b) {
      return false;
    }
    return new Date(a).getHours() === new Date(b).getHours() &&
           new Date(a).getMinutes() === new Date(b).getMinutes();
  }
}
